import React, { useState, useEffect } from "react";
import { getSupabase } from "./supabaseClient";
import "../css/RecentAudios.css";
import {
  HStack,
  Input,
  Button,
  Box,
  Text,
  Center,
  Select,
} from "@chakra-ui/react";
import {
  bucketName_files,
  tableName,
  data_column,
  prompts,
  file_reference_column,
  supabaseUrl,
  noShowTableName,
} from "./env.js";
import jsonToFormattedText from "./JSONToMarkdown.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "react-modal";
import ResultsTable from "./table/ResultsTable";
import { sortObjectsByDateTime } from "../utils/utils.js";
import {
  hydrateScheduleRowsTimezoneSource,
  fetchViewerIanaTimezone,
} from "../utils/scheduleRowTimezone.js";

const NoShows = ({ newAudioCreated }) => {
  const [allRecentAudios, setAllRecentAudios] = useState(undefined);

  const [recentAudios, setRecentAudios] = useState([]);
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [previousFolderPath, setPreviousFolderPath] = useState("");
  const [showAudios, setShowAudios] = useState(false);
  const [currentFolderFiles, setCurrentFolderFiles] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [viewerTimezone, setViewerTimezone] = useState("America/New_York");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    (async () => {
      const supabase = await getSupabase();
      setViewerTimezone(await fetchViewerIanaTimezone(supabase));
    })();
  }, []);

  const handleViewAudioFiles = (jsonContent) => {
    try {
      const jsonData = JSON.parse(jsonContent);
      const formattedText = jsonToFormattedText(jsonData);
      openHTMLInNewTab(formattedText);
    } catch (error) {
      console.error("Error parsing JSON content:", error);
      alert("Error parsing JSON content");
    }
  };

  const getRecentAudios = async (page = 1) => {
    const supabase = await getSupabase();
    try {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();

      if (sessionError) {
        console.error("Error getting session:", sessionError);
        return;
      }

      const userId = sessionData?.session?.user?.id;

      if (!userId) {
        console.error(
          "User ID not found in session. Cannot proceed with upload."
        );
        return;
      }

      console.log("User ID:", userId);

      const { data: userData, userError } = await supabase
        .from("users")
        .select("role")
        .eq("email", sessionData?.session?.user?.email);

      if (userError) {
        console.error("Error fetching user role:", userError);
      } else {
        const role = userData[0].role;
        console.log(role);

        let query = supabase
          .from(noShowTableName)
          .select("*", { count: "exact" });

        if (!["admin", "doctor"].includes(role)) {
          query = query.eq("user_id", userId);
        }

        let { data, error, count } = await query;

        if (error) {
          console.error("Error fetching recent audios:", error);
        } else {
          console.log("Recent audios fetched successfully:", data);
          // setRecentAudios(data);
          data = sortObjectsByDateTime(data, "date_", "time_");
          data = await hydrateScheduleRowsTimezoneSource(supabase, data);

          setAllRecentAudios(data);

          setShowAudios(true);
          // setTotalPages(Math.ceil(count / itemsPerPage));
          setTotalPages(Math.max(1, Math.ceil(count / itemsPerPage)));
          return data;
        }
      }
    } catch (error) {
      console.error("Error fetching recent audios:", error);
    }
  };

  useEffect(() => {
    if (allRecentAudios) {
      setRecentAudios(() =>
        allRecentAudios.slice(
          (currentPage - 1) * itemsPerPage,
          currentPage * itemsPerPage
        )
      );
    } else {
      getRecentAudios().then((data) => {
        setRecentAudios(
          data.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
          )
        );
      });
    }
  }, [newAudioCreated, currentPage, itemsPerPage]);

  // useEffect(() => {
  //   const checkAudioUrls = async () => {
  //     const urlValidity = {};

  //     const fetchPromises = recentAudios.map(async (audio) => {
  //       if (audio.audio_url) {
  //         try {
  //           const response = await fetch(audio.audio_url, { method: "HEAD" });
  //           urlValidity[audio.id] =
  //             response.ok && response.headers.get("Content-Length") !== "0";
  //         } catch {
  //           urlValidity[audio.id] = false;
  //         }
  //       } else {
  //         urlValidity[audio.id] = false;
  //       }
  //     });

  //     await Promise.all(fetchPromises);
  //     setValidAudioUrls(urlValidity);
  //   };

  //   checkAudioUrls();
  // }, [recentAudios]);

  const openHTMLInNewTab = (htmlContent) => {
    const newWindow = window.open("", "_blank");
    newWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>HTML Viewer</title>
        </head>
        <body>
          <div id="content">${htmlContent}</div>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  const handleViewFolderFiles = async (folderPath) => {
    const supabase = await getSupabase();
    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath);

    if (error) {
      console.error("Error fetching files from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
      setModalIsOpen(true);
    }
  };

  const handleGoUpOneLevel = async () => {
    const supabase = await getSupabase();
    setPreviousFolderPath(currentFolderPath);
    setCurrentFolderPath("");
    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list("");

    if (error) {
      console.error("Error fetching folders from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
    }
  };

  const handleGoDownOneLevel = async () => {
    handleViewFolderFiles(previousFolderPath);
  };

  const handleViewReports = async (folderPath) => {
    const supabase = await getSupabase();
    setCurrentFolderPath(folderPath);
    if (!folderPath) {
      setCurrentFolderFiles([]);
      setModalIsOpen(true);
      return;
    }

    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath);

    if (error) {
      console.error("Error fetching files from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
      setModalIsOpen(true);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  return (
    <Center className="recent-audios-container" flexDirection="column">
      <div className="recent-audios-container">
        <Text align="center" fontSize="2xl" fontWeight="bold" mb={4}>
          No Shows
        </Text>
        {showAudios &&
          (recentAudios.length > 0 ? (
            <>
              <ResultsTable
                results={recentAudios}
                setResults={setRecentAudios}
                prompts={prompts}
                selectedRows={[]} // No row selection needed here
                handleCheckboxChange={() => {}} // No-op
                handleViewJSONFiles={handleViewAudioFiles}
                handleViewFolderFiles={handleViewReports}
                currentFolderPath={currentFolderPath}
                previousFolderPath={previousFolderPath}
                handleGoUpOneLevel={handleGoUpOneLevel}
                handleGoDownOneLevel={handleGoDownOneLevel}
                supabaseUrl={supabaseUrl}
                bucketName_files={bucketName_files}
                modalIsOpen={modalIsOpen}
                setModalIsOpen={setModalIsOpen}
                currentFolderFiles={currentFolderFiles}
                showCheckboxes={false}
                showFiles={false}
                viewerTimezone={viewerTimezone}
              />
              <HStack spacing={4} justify="center" mt={4}>
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Text>
                  Page {currentPage} of {totalPages}
                </Text>
                <Button
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  width="auto"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                </Select>
              </HStack>
            </>
          ) : (
            <Text align="center" mt={4} fontSize="lg">
              You have no no-shows.
            </Text>
          ))}
      </div>
    </Center>
  );
};

export default NoShows;
